import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Refresh, ErrorOutline, Replay, Cancel } from "@material-ui/icons";
import { axiosInstance } from "../../Utils/ExpressAPI";

/**
 * Supervision de la file d'envoi vers le SGL (sections 24 a 27).
 *
 * Ce que cet ecran rend visible et qui ne l'etait pas : un envoi qui echoue
 * ne disparait pas. Il reste ici, avec son type d'erreur, son nombre de
 * tentatives et un bouton pour relancer. Un dossier ne se perd pas en silence.
 */

const ETATS = {
  erreur: { libelle: "En erreur", fond: "#FEF2F2", texte: "#991B1B" },
  en_attente_annulation: { libelle: "Annulable", fond: "#FFFBEB", texte: "#92400E" },
  en_file: { libelle: "En file", fond: "#EFF6FF", texte: "#1E40AF" },
  preparation: { libelle: "Préparation", fond: "#EFF6FF", texte: "#1E40AF" },
  envoi: { libelle: "Envoi en cours", fond: "#EFF6FF", texte: "#1E40AF" },
  envoye: { libelle: "Envoyé", fond: "#ECFDF5", texte: "#065F46" },
  annule: { libelle: "Annulé", fond: "#F3F4F6", texte: "#4B5563" },
};

const ERREURS = {
  reseau: "Réseau",
  authentification: "Authentification refusée",
  serveur_inaccessible: "Serveur injoignable",
  fichier_refuse: "Fichier refusé",
  nommage_incorrect: "Nommage incorrect",
  doublon: "Doublon",
  inconnue: "Erreur inconnue",
};

const useStyles = makeStyles(() => ({
  page: { padding: 24, maxWidth: 1200, margin: "0 auto" },
  entete: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  aide: { color: "#6B7280", fontSize: 13, margin: "8px 0 20px", maxWidth: 720 },
  compteurs: { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" },
  vide: { padding: 48, textAlign: "center", color: "#6B7280" },
  erreurDetail: { fontSize: 12, color: "#991B1B", marginTop: 2 },
  meta: { fontSize: 12, color: "#6B7280" },
}));

// Le jeton est pose par l'intercepteur de axiosInstance (Utils/ExpressAPI.js).
const entetes = () => ({});

function enDate(v) {
  if (!v) return null;
  if (v._seconds) return new Date(v._seconds * 1000);
  if (v.seconds) return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

const heure = (v) => {
  const d = enDate(v);
  return d ? d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—";
};

export default function FileEnvoi() {
  const classes = useStyles();
  const labId = localStorage.getItem("currentLab");

  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [action, setAction] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const { data } = await axiosInstance.get("transmissions-file", {
        params: { laboratoryId: labId },
        headers: entetes(),
      });
      setDonnees(data);
    } catch (e) {
      setErreur(
        e?.response?.data?.error ||
          "La file n'a pas pu être chargée. Le service est peut-être encore en cours de déploiement."
      );
    } finally {
      setChargement(false);
    }
  }, [labId]);

  useEffect(() => {
    charger();
    // La file bouge toute seule : la fenêtre d'annulation s'écoule, les envois
    // partent. Un rafraîchissement régulier évite d'afficher un état périmé.
    const t = setInterval(charger, 15000);
    return () => clearInterval(t);
  }, [charger]);

  const agir = async (id, quoi) => {
    setAction(id);
    try {
      const { data } = await axiosInstance.post(
        `transmissions-file/entrees/${id}/${quoi}`,
        {},
        { headers: entetes() }
      );
      if (data.raison) setErreur(data.raison);
      await charger();
    } catch (e) {
      setErreur(e?.response?.data?.error || "L'action a échoué.");
    } finally {
      setAction(null);
    }
  };

  const entrees = donnees?.entrees || [];

  return (
    <div className={classes.page}>
      <div className={classes.entete}>
        <Typography variant="h5">File d'envoi vers le SGL</Typography>
        <Button startIcon={<Refresh />} onClick={charger} style={{ textTransform: "none" }}>
          Rafraîchir
        </Button>
      </div>
      <div className={classes.aide}>
        Un envoi passe d'abord par une zone d'attente de trente secondes pendant
        laquelle il reste annulable. Un envoi qui échoue n'est jamais supprimé :
        il reste ici avec sa cause, et peut être relancé.
      </div>

      {donnees && (
        <div className={classes.compteurs}>
          {Object.entries(donnees.parEtat).map(([etat, n]) => {
            const e = ETATS[etat] || { libelle: etat, fond: "#F3F4F6", texte: "#4B5563" };
            return (
              <Chip
                key={etat}
                label={`${e.libelle} : ${n}`}
                style={{ background: e.fond, color: e.texte, fontWeight: 600 }}
              />
            );
          })}
        </div>
      )}

      {chargement && !donnees && (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress />
        </Box>
      )}

      {erreur && (
        <Paper
          style={{
            padding: 16,
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            gap: 12,
            color: "#991B1B",
          }}
        >
          <ErrorOutline />
          <span>{erreur}</span>
        </Paper>
      )}

      {donnees && entrees.length === 0 && (
        <Paper className={classes.vide}>
          Aucun envoi en cours. Rien n'est en attente ni en erreur.
        </Paper>
      )}

      {entrees.length > 0 && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>État</TableCell>
                <TableCell>Fichier</TableCell>
                <TableCell>Demandé</TableCell>
                <TableCell>Tentatives</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entrees.map((e) => {
                const etat = ETATS[e.etat] || {
                  libelle: e.etat,
                  fond: "#F3F4F6",
                  texte: "#4B5563",
                };
                return (
                  <TableRow key={e.id}>
                    <TableCell>
                      <Chip
                        size="small"
                        label={etat.libelle}
                        style={{ background: etat.fond, color: etat.texte, fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <div style={{ fontSize: 13 }}>{e.nomFichier}</div>
                      {e.typeErreur && (
                        <div className={classes.erreurDetail}>
                          {ERREURS[e.typeErreur] || e.typeErreur}
                          {e.derniereErreur ? ` — ${e.derniereErreur}` : ""}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className={classes.meta}>{heure(e.demandeLe)}</TableCell>
                    <TableCell className={classes.meta}>{e.tentatives || 0}</TableCell>
                    <TableCell align="right">
                      {e.etat === "en_attente_annulation" && (
                        <Button
                          size="small"
                          startIcon={<Cancel />}
                          style={{ textTransform: "none" }}
                          disabled={action === e.id}
                          onClick={() => agir(e.id, "annuler")}
                        >
                          Annuler
                        </Button>
                      )}
                      {e.etat === "erreur" && (
                        <Button
                          size="small"
                          color="primary"
                          startIcon={<Replay />}
                          style={{ textTransform: "none" }}
                          disabled={action === e.id}
                          onClick={() => agir(e.id, "relancer")}
                        >
                          Relancer
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </div>
  );
}
